import { ApolloQueryResult } from '@apollo/client';
import * as queryTypes from './graphqlTypes';
import setters from '../actions';
import getters from '../getters';
import { resetAuth } from './action';
import { resetBanner } from '../banner/action';
import { resetForm } from '../form/action';
import { resetModal } from '../modal/action';
import { resetPrint } from '../print/action';
import { resetProduct } from '../product/action';
import { resetTicket } from '../ticket/action';
import { resetUser } from '../user/action';

export const emitSessionAuthentication =
  () => (dispatch: DispatchType, getState: GetStateType) => {
    const {
      auth: { getToken },
    } = getters(getState());
    const {
      socket: { SOCKET },
    } = setters(dispatch);

    const { access_token } = getToken();
    SOCKET.emit('auth', {
      token: access_token,
    });
  };

export const onConfirmLogout = () => (dispatch: DispatchType) => {
  const {
    socket: { SOCKET },
  } = setters(dispatch);
  SOCKET.on('confirm-logout', () => {
    const {
      modal: { setModal },
    } = setters(dispatch);
    setModal('single.confirmSession', true);
  });
};

export const emitLogoutConfirmation = () => (dispatch: DispatchType) => {
  const {
    socket: { SOCKET },
  } = setters(dispatch);
  SOCKET.emit('logout-confirmation');
};

export const login = () => (dispatch: DispatchType, getState: GetStateType) =>
  new Promise<boolean>((resolve, reject) => {
    const {
      auth: { getToken, getProfile, authenticated },
      user: { findUser },
    } = setters(dispatch);
    getToken()
      .then(() => {
        getProfile()
          .then(() => {
            findUser()
              .then(() => {
                authenticated();
                resolve(true);
              })
              .catch((error) => {
                reject(error);
              });
          })
          .catch((error) => {
            reject(error);
          });
      })
      .catch((error) => {
        reject(error);
      });
  });

export const getToken =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<ApolloQueryResult<GetTokenQuery>>((resolve, reject) => {
      const {
        form: { getForm },
      } = getters(getState());
      const {
        graphql: { QUERY },
        auth: { setToken },
      } = setters(dispatch);
      QUERY<GetTokenQuery>({
        query: queryTypes.GET_TOKEN,
        variables: { getTokenRequest: getForm('login') },
      })
        .then((result) => {
          setToken(result.data.GetToken);
          localStorage.setItem('es_token', result.data.GetToken.access_token);
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const getKibanaToken = () => (dispatch: DispatchType) =>
  new Promise<KibanaToken>((resolve, reject) => {
    const {
      graphql: { QUERY },
      auth: { setAuth, getKibanaDashboards },
    } = setters(dispatch);
    QUERY<GetKibanaTokenQuery>({
      query: queryTypes.GET_KIBANA_TOKEN,
    })
      .then((result) => {
        const token = result.data.getKibanaToken;
        setAuth('kibana.token', token);
        getKibanaDashboards()
          .then(() => {
            resolve(token);
          })
          .catch(reject);
      })
      .catch(reject);
  });

export const getKibanaDashboards = () => (dispatch: DispatchType) =>
  new Promise<KibanaDasboard[]>((resolve, reject) => {
    const {
      graphql: { QUERY },
      auth: { setAuth },
    } = setters(dispatch);
    QUERY<GetKibanaDashboardsQuery>({
      query: queryTypes.GET_KIBANA_DASHBOARDS,
    })
      .then((result) => {
        const dashboards = result.data.getKibanaDashboards.data;
        setAuth('kibana.dashboards', dashboards);
        resolve(dashboards);
      })
      .catch((error) => {
        reject(error);
      });
  });

export const getProfile = () => (dispatch: DispatchType) =>
  new Promise<ApolloQueryResult<GetProfileQuery>>((resolve, reject) => {
    const {
      graphql: { QUERY },
      auth: { setProfile, emitSessionAuthentication, setToken, authenticated },
    } = setters(dispatch);

    QUERY<GetProfileQuery>({ query: queryTypes.GET_PROFILE })
      .then((result) => {
        emitSessionAuthentication();
        setProfile(result.data.GetProfile);
        // Em reload, redux state.auth.token está vazio mas localStorage.es_token
        // tem o JWT (Apollo lê de lá). Re-hidrata o redux + sinaliza autenticado
        // pra widgets como o ArqueiroBootstrap reagirem em login E reload.
        const persisted = localStorage.getItem('es_token');
        if (persisted) {
          setToken({ access_token: persisted } as any);
        }
        authenticated();
        resolve(result);
      })
      .catch((error) => {
        dispatch(resetAuth());
        dispatch(resetBanner());
        dispatch(resetForm());
        dispatch(resetModal());
        dispatch(resetPrint());
        dispatch(resetProduct());
        dispatch(resetTicket());
        dispatch(resetUser());
        reject(error);
      });
  });

const authActionCreators = (dispatch: <T>(action: any) => T) =>
  Object.assign(
    {},
    {
      emitSessionAuthentication: () => dispatch(emitSessionAuthentication()),
      emitLogoutConfirmation: () => dispatch(emitLogoutConfirmation()),
      onConfirmLogout: () => dispatch(onConfirmLogout()),
      getToken: () =>
        dispatch<Promise<ApolloQueryResult<GetTokenQuery>>>(getToken()),
      getProfile: () =>
        dispatch<Promise<ApolloQueryResult<GetProfileQuery>>>(getProfile()),
      login: () => dispatch<Promise<boolean>>(login()),
      getKibanaToken: () => dispatch<Promise<KibanaToken>>(getKibanaToken()),
      getKibanaDashboards: () =>
        dispatch<Promise<KibanaDasboard[]>>(getKibanaDashboards()),
    },
  );

export default authActionCreators;
